// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import { List } from '../List.js';
import Image from '../Image.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import TabularData from '../TabularData.js';
import DraftImage from '../DraftImage.js';

const GUTTER = 4;

export function SolidifyAnnotatedFigure(props: {|
  children: [
    (
      | Element<typeof Image>
      | Element<typeof DraftImage>
      | Element<typeof CartesianPlane>
      | Element<typeof NumberLine>
      | Element<typeof StemAndLeafPlot>
      | Element<typeof BoxAndWhiskerPlot>
      | Element<typeof TabularData>
    ),
    Element<typeof SolidifyFigureAnnotation>,
  ],
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    SolidifyAnnotatedFigure,
  );

  const figureCell = React.useRef(null);
  const measurer = React.useRef(null);

  // Validate that annotations do not extend beyond the bottom of the figure
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    const { current: figureCellNode } = figureCell;
    const { current: measurerNode } = measurer;
    if (figureCellNode === null || measurerNode === null) return;
    const cellRect = figureCellNode.getBoundingClientRect();
    const figureRect = measurerNode.getBoundingClientRect();
    if (cellRect.height > figureRect.height) {
      const annotationElement = props.children[1];
      addErrors([
        {
          message: `Your annotation is too long. The annotation cannot exceed the height of the figure. Your annotation is ${cellRect.height}px tall, but must be <= ${figureRect.height}px`,
          ...annotationElement.props._sourceLocations?.SolidifyFigureAnnotation,
        },
      ]);
    }
  });

  // Validate that the width of the figure fits within the available space
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    const { current: measurerNode } = measurer;
    if (measurerNode === null) return;
    const availableWidth = measurerNode.getBoundingClientRect().width;
    const widestElement = getWidestElementInTree(measurerNode);
    if (widestElement > availableWidth) {
      const figureElement = props.children[0];
      // $FlowIgnore withAnnotation HoC confuses things
      const componentName = figureElement.type.__wrappedComponent
        ? // $FlowIgnore withAnnotation HoC confuses things
          figureElement.type.__wrappedComponent.name
        : figureElement.type.name;
      addErrors([
        {
          message: `The figure is too wide for the available space. It is ${widestElement}px wide, but must be <= ${availableWidth}px`,
          // $FlowIgnore withAnnotation HoC confuses things
          ...figureElement.props._sourceLocations?.[componentName],
        },
      ]);
    }
  });

  if (failedValidation) return null;

  const [figure, annotation] = React.Children.toArray(props.children);

  return (
    <div
      style={{
        display: 'table',
        tableLayout: 'fixed',
        width: '100%',
        breakInside: 'avoid',
      }}
    >
      <div
        ref={figureCell}
        style={{
          display: 'table-cell',
          width: `calc(50% - ${GUTTER / 2}px)`,
          verticalAlign: 'top',
        }}
      >
        {/* flow-root forces a block formatting context so that measuring
            the height will include any margins defined in descendents. */}
        <div ref={measurer} style={{ display: 'flow-root' }}>
          {figure}
        </div>
      </div>
      <div style={{ display: 'table-cell', width: GUTTER }} />
      <div
        style={{
          display: 'table-cell',
          width: `calc(50% - ${GUTTER / 2}px)`,
          overflow: 'hidden',
          verticalAlign: 'top',
        }}
      >
        {annotation}
      </div>
    </div>
  );
}
SolidifyAnnotatedFigure.spec = {
  children: t.tuple([
    t.unionOfTypes(
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(TabularData),
    ),
    t.elementType(SolidifyFigureAnnotation),
  ]),
};

export function SolidifyFigureAnnotation(props: {|
  children: ChildrenArray<Element<typeof Text | typeof List>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    SolidifyFigureAnnotation,
  );
  if (failedValidation) return null;
  return props.children;
}
SolidifyFigureAnnotation.spec = {
  children: t.childrenArray(
    t.unionOfTypes(t.elementType(Text), t.elementType(List)),
  ),
};

function getWidestElementInTree(node) {
  return Math.max(
    node.getBoundingClientRect().width,
    ...Array.from(node.children).map((child) => getWidestElementInTree(child)),
  );
}
